import React from 'react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Calendar, MapPin, Tag, X } from 'lucide-react';

interface Sport {
  id: string;
  name: string;
}

interface EventCategory {
  id: string;
  name: string;
}

interface EventFiltersProps {
  genderFilter: string;
  setGenderFilter: (value: string) => void;
  sportFilter: string;
  setSportFilter: (value: string) => void;
  monthFilter: string;
  setMonthFilter: (value: string) => void;
  categoryFilters: string[];
  setCategoryFilters: (value: string[]) => void;
  availableSports: Sport[];
  availableMonths: string[];
  availableCategories: EventCategory[];
  // New filter props
  dateFilter?: string;
  setDateFilter?: (value: string) => void;
  stateFilter?: string;
  setStateFilter?: (value: string) => void;
  availableStates?: string[];
}

const EventFilters: React.FC<EventFiltersProps> = ({
  genderFilter,
  setGenderFilter,
  sportFilter,
  setSportFilter,
  monthFilter,
  setMonthFilter,
  categoryFilters,
  setCategoryFilters,
  availableSports,
  availableMonths,
  availableCategories,
  dateFilter = '',
  setDateFilter,
  stateFilter = 'all',
  setStateFilter,
  availableStates = [],
}) => {
  const handleCategoryToggle = (categoryId: string) => {
    if (categoryFilters.includes(categoryId)) {
      setCategoryFilters(categoryFilters.filter(id => id !== categoryId));
    } else {
      setCategoryFilters([...categoryFilters, categoryId]);
    }
  };

  const clearAllFilters = () => {
    setGenderFilter('all');
    setSportFilter('all');
    setMonthFilter('all');
    setCategoryFilters([]);
    setDateFilter?.('');
    setStateFilter?.('all');
  };

  const hasActiveFilters = genderFilter !== 'all' || sportFilter !== 'all' || monthFilter !== 'all' || categoryFilters.length > 0 || (dateFilter && dateFilter !== '') || (stateFilter && stateFilter !== 'all');

  return (
    <div className="bg-card border rounded-lg p-4 mb-6">
      <div className="flex flex-wrap gap-4 items-center mb-4">
        <h3 className="text-lg font-semibold">Filter Events</h3>
        
        {/* Date Filter */}
        {setDateFilter && (
          <div className="min-w-40">
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground mb-1">
              <Calendar className="w-3.5 h-3.5" />
              <span>Date</span>
            </div>
            <Input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="w-full"
            />
          </div>
        )}

        {/* State Filter */}
        {setStateFilter && availableStates.length > 0 && (
          <div className="min-w-40">
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground mb-1">
              <MapPin className="w-3.5 h-3.5" />
              <span>State</span>
            </div>
            <Select value={stateFilter} onValueChange={setStateFilter}>
              <SelectTrigger>
                <SelectValue placeholder="All States" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All States</SelectItem>
                {availableStates.map((state) => (
                  <SelectItem key={state} value={state}>
                    {state}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Gender Filter */}
        <div className="min-w-32">
          <div className="text-sm text-muted-foreground mb-1">Gender</div>
          <Select value={genderFilter} onValueChange={setGenderFilter}>
            <SelectTrigger>
              <SelectValue placeholder="Gender" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Genders</SelectItem>
              <SelectItem value="Men">Boys</SelectItem>
              <SelectItem value="Women">Girls</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Sport Filter */}
        <div className="min-w-40">
          <div className="text-sm text-muted-foreground mb-1">Sport</div>
          <Select value={sportFilter} onValueChange={setSportFilter}>
            <SelectTrigger>
              <SelectValue placeholder="Sport" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sports</SelectItem>
              {availableSports.map((sport) => (
                <SelectItem key={sport.id} value={sport.id}>
                  {sport.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Month Filter */}
        <div className="min-w-40">
          <div className="text-sm text-muted-foreground mb-1">Month</div>
          <Select value={monthFilter} onValueChange={setMonthFilter}>
            <SelectTrigger>
              <SelectValue placeholder="Month" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Months</SelectItem>
              {availableMonths.map((month) => (
                <SelectItem key={month} value={month}>
                  {month}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Clear Filters */}
        {hasActiveFilters && (
          <button
            onClick={clearAllFilters}
            className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mt-auto"
          >
            <X className="w-4 h-4" />
            Clear All
          </button>
        )}
      </div>

      {/* Category Filter */}
      <div>
        <div className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground mb-2">
          <Tag className="w-3.5 h-3.5" />
          <span>Categories</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {availableCategories.map((category) => {
            const isSelected = categoryFilters.includes(category.id);
            return (
              <Badge
                key={category.id}
                variant={isSelected ? "default" : "outline"}
                className="cursor-pointer hover:opacity-80"
                onClick={() => handleCategoryToggle(category.id)}
              >
                {category.name}
                {isSelected && <X className="w-3 h-3 ml-1" />}
              </Badge>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default EventFilters;
